/**
 * Pure FactSet number-format enforcement shared by executor and host paths.
 */

import { diag } from "./aiDiag";
import {
  normalizeFactSetNumberFormat,
  type FactSetFormatHint,
} from "./factsetValueSemantics";
import {
  trackedCellsInRange,
  type FactSetFormulaFormatRegistry,
} from "./factsetFormulaFormatRegistry";
import type {
  CellFormat,
  RangeModification,
  WorkbookModification,
} from "./types";

const FACTSET_FORMAT_GUARD = Symbol("factsetFormatGuard");

export interface FactSetFormatGuard {
  mode: "percentage_points" | "reset_stale_percentage_points";
  expectedFormula?: string;
  requiresSuccessfulWrite: boolean;
  confirmationSource?: "move_range";
}

type GuardedRangeModification = RangeModification & {
  [FACTSET_FORMAT_GUARD]?: FactSetFormatGuard;
};

export function getFactSetFormatGuard(
  range: RangeModification,
): FactSetFormatGuard | undefined {
  return (range as GuardedRangeModification)[FACTSET_FORMAT_GUARD];
}

interface FactSetFormatOverride {
  sheetName: string;
  address: string;
  originalFormat?: string;
  correctedFormat: string;
  hint: FactSetFormatHint;
  guard: FactSetFormatGuard;
}

/**
 * Add cell-specific percentage-point formats after broad model-authored
 * formatting. This preserves mixed-range styling while ensuring only known
 * FactSet return cells receive a literal percent sign.
 */
export function normalizeFactSetFormatsForModification(
  modifications: WorkbookModification,
  registry: FactSetFormulaFormatRegistry,
  workbookName?: string,
): WorkbookModification {
  if (!modifications.worksheets?.length) return modifications;
  const analysisModifications: WorkbookModification = {
    ...modifications,
    worksheets: modifications.worksheets.map((worksheet) => ({
      ...worksheet,
      sheetName:
        typeof worksheet.sheetName === "string" &&
        worksheet.sheetName.trim().length > 0
          ? worksheet.sheetName
          : "__FACTSET_ACTIVE_SHEET__",
    })),
  };
  const previewCells = registry.previewCells(
    analysisModifications,
    workbookName,
  );
  const existingCells = registry.previewCells({}, workbookName);
  const writtenCells = registry.previewFormulaWrites(
    analysisModifications,
    workbookName,
  );
  if (
    previewCells.length === 0 &&
    writtenCells.length === 0 &&
    existingCells.length === 0
  ) {
    return modifications;
  }

  const overridesBySheet = new Map<
    string,
    Map<string, FactSetFormatOverride>
  >();
  const getSheetOverrides = (
    sheetName: string,
  ): Map<string, FactSetFormatOverride> => {
    const key = sheetName.trim().toLowerCase();
    const existing = overridesBySheet.get(key);
    if (existing) return existing;
    const created = new Map<string, FactSetFormatOverride>();
    overridesBySheet.set(key, created);
    return created;
  };

  const finalCellsByAddress = new Map(
    previewCells.map((cell) => [trackedAddressKey(cell.sheetName, cell.address), cell]),
  );
  for (const previousCell of existingCells) {
    if (previousCell.hint.semantics !== "percentage_points") continue;
    const finalCell = finalCellsByAddress.get(
      trackedAddressKey(previousCell.sheetName, previousCell.address),
    );
    if (finalCell?.hint.semantics === "percentage_points") continue;
    const overwrite = findExplicitContentOverwrite(
      analysisModifications,
      previousCell.sheetName,
      previousCell.address,
      workbookName,
    );
    if (
      !overwrite.found ||
      hasExplicitNumberFormat(
        analysisModifications,
        previousCell.sheetName,
        previousCell.address,
        workbookName,
      )
    ) {
      continue;
    }
    getSheetOverrides(previousCell.sheetName).set(
      previousCell.address.toUpperCase(),
      {
        sheetName: previousCell.sheetName,
        address: previousCell.address,
        correctedFormat: "General",
        hint: previousCell.hint,
        guard: {
          mode: "reset_stale_percentage_points",
          ...(overwrite.formula
            ? { expectedFormula: overwrite.formula }
            : {}),
          requiresSuccessfulWrite: true,
        },
      },
    );
  }

  // A new percentage-point formula must override an inherited percent format
  // even when the model omits number formatting entirely.
  for (const cell of writtenCells) {
    if (
      cell.hint.semantics !== "percentage_points" &&
      cell.hint.semantics !== "decimal_fraction"
    ) {
      continue;
    }
    if (
      !cell.hint.recommendedNumberFormat
    ) {
      continue;
    }
    getSheetOverrides(cell.sheetName).set(cell.address.toUpperCase(), {
      sheetName: cell.sheetName,
      address: cell.address,
      correctedFormat: cell.hint.recommendedNumberFormat,
      hint: cell.hint,
      guard: {
        mode:
          cell.hint.semantics === "percentage_points"
            ? "percentage_points"
            : "reset_stale_percentage_points",
        expectedFormula: cell.formula,
        requiresSuccessfulWrite: true,
      },
    });
  }

  const visitFormat = (
    sheetName: string,
    address: string,
    format: CellFormat | undefined,
  ): void => {
    const numberFormat = getNumberFormatCode(format);
    if (!numberFormat) return;
    const tracked = trackedCellsInRange(
      previewCells,
      workbookName,
      sheetName,
      address,
    ).filter(
      (cell) =>
        cell.hint.semantics === "percentage_points" ||
        cell.hint.semantics === "decimal_fraction",
    );
    const sheetOverrides = getSheetOverrides(sheetName);
    for (const cell of tracked) {
      const cellKey = cell.address.toUpperCase();
      const existingOverride = sheetOverrides.get(cellKey);
      const moveDestination = isMoveDestinationCell(
        analysisModifications,
        sheetName,
        cell.address,
      );
      const normalized = normalizeFactSetNumberFormat(
        numberFormat,
        cell.hint.semantics,
      );
      if (!normalized.changed) {
        // The requested format already displays the value's scale correctly,
        // so it supersedes the recommended default.
        sheetOverrides.delete(cellKey);
        continue;
      }
      sheetOverrides.set(cellKey, {
        sheetName,
        address: cell.address,
        originalFormat: numberFormat,
        correctedFormat: normalized.format,
        hint: cell.hint,
        guard:
          existingOverride?.guard ?? {
            mode: "percentage_points",
            expectedFormula: cell.formula,
            requiresSuccessfulWrite: moveDestination,
            ...(moveDestination
              ? { confirmationSource: "move_range" as const }
              : {}),
          },
      });
    }
  };

  for (const worksheet of analysisModifications.worksheets ?? []) {
    const sheetName =
      typeof worksheet.sheetName === "string"
        ? worksheet.sheetName.trim()
        : "";
    if (!sheetName) continue;
    for (const cell of worksheet.cells ?? []) {
      if (cell.address) visitFormat(sheetName, cell.address, cell.format);
    }
    for (const range of worksheet.ranges ?? []) {
      if (range.address) visitFormat(sheetName, range.address, range.format);
    }
  }

  const finalOverrides = Array.from(overridesBySheet.values()).flatMap(
    (sheetOverrides) => Array.from(sheetOverrides.values()),
  );
  if (finalOverrides.length === 0) return modifications;

  const lastWorksheetIndex = new Map<string, number>();
  analysisModifications.worksheets?.forEach((worksheet, index) => {
    if (worksheet.sheetName) {
      lastWorksheetIndex.set(
        worksheet.sheetName.trim().toLowerCase(),
        index,
      );
    }
  });
  const overridesByIndex = new Map<number, FactSetFormatOverride[]>();
  const deferredMoveOverridesBySheet = new Map<
    string,
    FactSetFormatOverride[]
  >();
  for (const override of finalOverrides) {
    if (
      override.guard.confirmationSource === "move_range" &&
      override.sheetName !== "__FACTSET_ACTIVE_SHEET__"
    ) {
      const key = override.sheetName.trim().toLowerCase();
      const entries = deferredMoveOverridesBySheet.get(key) ?? [];
      entries.push(override);
      deferredMoveOverridesBySheet.set(key, entries);
      continue;
    }
    const index = lastWorksheetIndex.get(
      override.sheetName.trim().toLowerCase(),
    );
    if (index === undefined) continue;
    const entries = overridesByIndex.get(index) ?? [];
    entries.push(override);
    overridesByIndex.set(index, entries);
  }

  for (const override of finalOverrides) {
    diag("format", "factset_percent_format_normalized", {
      sheetName: override.sheetName,
      address: override.address,
      dataItem: override.hint.dataItem,
      source: override.hint.source,
      originalFormat: override.originalFormat,
      correctedFormat: override.correctedFormat,
    });
  }
  const worksheets = modifications.worksheets.map((worksheet, index) => {
    const overrides = overridesByIndex.get(index);
    if (!overrides?.length) return worksheet;
    return {
      ...worksheet,
      ranges: [
        ...(worksheet.ranges ?? []),
        ...overrides.map(createGuardedFormatRange),
      ],
    };
  });
  for (const overrides of Array.from(
    deferredMoveOverridesBySheet.values(),
  )) {
    const sheetName = overrides[0]?.sheetName;
    if (!sheetName) continue;
    worksheets.push({
      sheetName,
      ranges: overrides.map(createGuardedFormatRange),
    });
  }
  return { ...modifications, worksheets };
}

function findExplicitContentOverwrite(
  modifications: WorkbookModification,
  sheetName: string,
  address: string,
  workbookName?: string,
): { found: boolean; formula?: string } {
  let result: { found: boolean; formula?: string } = { found: false };
  for (const worksheet of modifications.worksheets ?? []) {
    if (
      (worksheet.sheetName ?? "").trim().toLowerCase() !==
      sheetName.trim().toLowerCase()
    ) {
      continue;
    }
    for (const cell of worksheet.cells ?? []) {
      if (
        cell.action !== "set" ||
        (cell.formula === undefined && cell.value === undefined) ||
        trackedCellsInRange(
          [
            {
              workbookName: workbookName ?? "",
              sheetName,
              address,
              formula: "",
              hint: {
                semantics: "unknown",
                confidence: "low",
                source: "unknown",
              },
            },
          ],
          workbookName,
          sheetName,
          cell.address,
        ).length === 0
      ) {
        continue;
      }
      const formula =
        typeof cell.formula === "string"
          ? cell.formula
          : typeof cell.value === "string" && cell.value.trim().startsWith("=")
            ? cell.value
            : undefined;
      result = {
        found: true,
        ...(formula ? { formula } : {}),
      };
    }
    for (const range of worksheet.ranges ?? []) {
      const clearsContentsOnly =
        range.action === "clear" &&
        (range.clear?.clearType ?? "All") === "Contents";
      if (
        (range.action === "fill" || clearsContentsOnly) &&
        trackedCellsInRange(
          [
            {
              workbookName: workbookName ?? "",
              sheetName,
              address,
              formula: "",
              hint: {
                semantics: "unknown",
                confidence: "low",
                source: "unknown",
              },
            },
          ],
          workbookName,
          sheetName,
          range.address,
        ).length > 0
      ) {
        result = { found: true };
      }
    }
  }
  return result;
}

function hasExplicitNumberFormat(
  modifications: WorkbookModification,
  sheetName: string,
  address: string,
  workbookName?: string,
): boolean {
  const tracked = [
    {
      workbookName: workbookName ?? "",
      sheetName,
      address,
      formula: "",
      hint: {
        semantics: "unknown" as const,
        confidence: "low" as const,
        source: "unknown" as const,
      },
    },
  ];
  for (const worksheet of modifications.worksheets ?? []) {
    if (
      (worksheet.sheetName ?? "").trim().toLowerCase() !==
      sheetName.trim().toLowerCase()
    ) {
      continue;
    }
    for (const cell of worksheet.cells ?? []) {
      if (
        getNumberFormatCode(cell.format) &&
        trackedCellsInRange(
          tracked,
          workbookName,
          sheetName,
          cell.address,
        ).length > 0
      ) {
        return true;
      }
    }
    for (const range of worksheet.ranges ?? []) {
      if (
        getNumberFormatCode(range.format) &&
        trackedCellsInRange(
          tracked,
          workbookName,
          sheetName,
          range.address,
        ).length > 0
      ) {
        return true;
      }
    }
  }
  return false;
}

function trackedAddressKey(sheetName: string, address: string): string {
  return `${sheetName.trim().toLowerCase()}!${address
    .replace(/\$/g, "")
    .toUpperCase()}`;
}

function isMoveDestinationCell(
  modifications: WorkbookModification,
  sheetName: string,
  address: string,
): boolean {
  const target = parseFiniteCellRange(address);
  if (!target) return false;
  const targetSheet = sheetName.trim().toLowerCase();
  for (const worksheet of modifications.worksheets ?? []) {
    const enclosingSheet = (worksheet.sheetName ?? "").trim();
    for (const range of worksheet.ranges ?? []) {
      if (range.action !== "moveRange" || !range.moveRange?.destination) {
        continue;
      }
      const destinationSheet =
        range.moveRange.destinationSheetName?.trim() || enclosingSheet;
      if (destinationSheet.toLowerCase() !== targetSheet) continue;
      const destination = moveDestinationRange(
        range.address,
        range.moveRange.destination,
      );
      if (
        destination &&
        target.startRow >= destination.startRow &&
        target.endRow <= destination.endRow &&
        target.startColumn >= destination.startColumn &&
        target.endColumn <= destination.endColumn
      ) {
        return true;
      }
    }
  }
  return false;
}

interface FiniteCellRange {
  startRow: number;
  endRow: number;
  startColumn: number;
  endColumn: number;
}

function moveDestinationRange(
  sourceAddress: string,
  destinationAddress: string,
): FiniteCellRange | undefined {
  const source = parseFiniteCellRange(sourceAddress);
  const destination = parseFiniteCellRange(destinationAddress);
  if (!source || !destination) return undefined;
  return {
    startRow: destination.startRow,
    endRow: destination.startRow + source.endRow - source.startRow,
    startColumn: destination.startColumn,
    endColumn:
      destination.startColumn + source.endColumn - source.startColumn,
  };
}

function parseFiniteCellRange(address: string): FiniteCellRange | undefined {
  const bare = address.trim().split("!").pop()?.replace(/\$/g, "") ?? "";
  const match = bare.match(
    /^([A-Za-z]+)(\d+)(?::([A-Za-z]+)(\d+))?$/,
  );
  if (!match) return undefined;
  const startColumn = formatColumnIndex(match[1]);
  const startRow = Number(match[2]) - 1;
  const endColumn = match[3] ? formatColumnIndex(match[3]) : startColumn;
  const endRow = match[4] ? Number(match[4]) - 1 : startRow;
  if (
    startRow < 0 ||
    endRow < startRow ||
    startColumn < 0 ||
    endColumn < startColumn
  ) {
    return undefined;
  }
  return { startRow, endRow, startColumn, endColumn };
}

function formatColumnIndex(columnName: string): number {
  let value = 0;
  for (const char of columnName.toUpperCase()) {
    value = value * 26 + char.charCodeAt(0) - 64;
  }
  return value - 1;
}

function createGuardedFormatRange(
  override: FactSetFormatOverride,
): RangeModification {
  const range: GuardedRangeModification = {
    address: override.address,
    action: "format",
    format: {
      numberFormat: { format: override.correctedFormat },
    },
  };
  Object.defineProperty(range, FACTSET_FORMAT_GUARD, {
    value: override.guard,
    enumerable: false,
  });
  return range;
}

function getNumberFormatCode(
  format: CellFormat | undefined,
): string | undefined {
  const numberFormat = format?.numberFormat as
    | string
    | { format?: unknown }
    | undefined;
  if (typeof numberFormat === "string") return numberFormat;
  return typeof numberFormat?.format === "string"
    ? numberFormat.format
    : undefined;
}
